import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { API_URL } from '../config/appConfig';

// Create the context
const FeatureToggleContext = createContext();

// Default state for features - everything disabled by default for safety
const defaultFeatures = {
  enableGrades: false,
  enableClasses: false,
  enableSubjects: false,
  enableStudents: false,
  enableTeachers: false,
  enableNotifications: false,
  enableContactDeveloper: false,
  enableContact: false,
  enableCalendar: false,
  enableSchedule: false,
  enableScheduleWizard: false,
  enableRatingSystem: false,
  enableRatings: false,
  enableAttendance: false,
  enableAnalytics: false,
  enableUserManagement: false,
  enableSchoolSettings: false,
  enableSystemMaintenance: false,
  enableBugReports: false,
  enableDirections: false,
  enablePatchNotes: false,
  enableStudentProgress: false,
  enablePayments: false
};

/**
 * Provider component for feature toggle functionality
 * This will fetch feature toggles from the new comprehensive permission system
 */
export const FeatureToggleProvider = ({ children }) => {
  // Get auth state from Redux store
  const { user } = useSelector((state) => state.auth);
  const token = user?.token; // Token is INSIDE the user object
  const [features, setFeatures] = useState(defaultFeatures);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch feature toggles from the new permission system
  useEffect(() => {
    // If no user or no token, reset features to default (disabled) but KEEP WATCHING
    if (!user || !token) {
      setFeatures(defaultFeatures);
      setLoading(false);
      setError(null);
      return; // EXIT but useEffect will retrigger when user/token change
    }

    // Fetch feature toggles from the new permission system
    const fetchFeatureToggles = async () => {
      try {
        setLoading(true);
        setError(null);

        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        };

        // Use the new API endpoint for current user's school permissions
        const response = await axios.get(`${API_URL}/api/school-permissions/current`, config);

        if (response.data && response.data.success && response.data.data) {
          const { features: fetchedFeatures } = response.data.data;
          setFeatures(fetchedFeatures || defaultFeatures);
        } else {
          console.error('FeatureToggleProvider: Invalid response structure');
          setError('Invalid response from permission system');
          setFeatures(defaultFeatures);
        }
        
      } catch (error) {
        console.error('FeatureToggleProvider: Error fetching feature toggles:', error);
        
        // More detailed error logging
        if (error.response) {
          console.error('FeatureToggleProvider: Error response:', {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers
          });
          setError(`Server error: ${error.response.status} - ${error.response.data?.message || error.message}`);
        } else if (error.request) {
          console.error('FeatureToggleProvider: No response received:', error.request);
          setError('No response from server');
        } else {
          console.error('FeatureToggleProvider: Request setup error:', error.message);
          setError(`Request error: ${error.message}`);
        }
        
        // Fallback to default features on error
        setFeatures(defaultFeatures);
      } finally {
        setLoading(false);
      }
    };

    fetchFeatureToggles();
  }, [user, token]);

  // Helper function to check if a specific feature is enabled
  const isFeatureEnabled = (featureName) => {
    // For superadmin, all features are enabled by default
    if (user?.role === 'superadmin') {
      return true;
    }
    
    // Check if the feature exists in the loaded features
    if (features && featureName in features) {
      return features[featureName] === true;
    }
    
    // Default to false for safety
    return false;
  };

  // Helper function to get all enabled features
  const getEnabledFeatures = () => {
    if (user?.role === 'superadmin') {
      // Return all features as enabled for superadmin
      const allFeatures = {};
      Object.keys(defaultFeatures).forEach(key => {
        allFeatures[key] = true;
      });
      return allFeatures;
    }
    
    // Filter only enabled features for all other users (including admin)
    const enabledFeatures = {};
    Object.keys(features).forEach(key => {
      if (features[key] === true) {
        enabledFeatures[key] = true;
      }
    });
    
    return enabledFeatures;
  };

  // Helper function to refresh permissions (useful after updates)
  const refreshPermissions = async () => {
    if (!user || !token) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      const response = await axios.get(`${API_URL}/api/school-permissions/current`, config);
      
      if (response.data && response.data.success && response.data.data) {
        const { features: fetchedFeatures } = response.data.data;
        setFeatures(fetchedFeatures || defaultFeatures);
        console.log('FeatureToggleProvider: Permissions refreshed successfully');
      }
      
    } catch (error) {
      console.error('FeatureToggleProvider: Error refreshing permissions:', error);
      setError('Failed to refresh permissions');
    } finally {
      setLoading(false);
    }
  };

  // Expose the context value
  const contextValue = {
    features,
    loading,
    error,
    isFeatureEnabled,
    getEnabledFeatures,
    refreshPermissions,
    isSuperAdmin: user?.role === 'superadmin'
  };

  return (
    <FeatureToggleContext.Provider value={contextValue}>
      {children}
    </FeatureToggleContext.Provider>
  );
};

/**
 * Custom hook to use the feature toggle context
 */
export const useFeatureToggles = () => {
  const context = useContext(FeatureToggleContext);
  if (!context) {
    throw new Error('useFeatureToggles must be used within a FeatureToggleProvider');
  }
  return context;
};

export default FeatureToggleContext;
